import { z } from 'zod'
import {
  DraftVerificationIssueSchema,
  type DraftVerificationOutcome,
} from './verification/draft-verification.contract'

/**
 * Zod source of truth for the scribe draft-generation response (replaces the
 * former class-validator `generate-draft-response.dto.ts`). The `oc` contract
 * uses these as `.input()`/`.output()`; the visit SOAP schema reuses the
 * shared leaves (`rosEntrySchema`, `billingSuggestionSchema`).
 *
 * The draft response is ASSEMBLED server-side by the handler from the model's
 * tool_use, so nullable model fields are `.nullable()` (key present, value may
 * be null) — matching the former `!: T | null` shape — not bare `.optional()`.
 */

// ROS entry — reused by SoapNote.reviewOfSystemsStructured (whitelist-validated
// there, so both fields stay required strings, mirroring the DTO decorators).
export const rosEntrySchema = z.object({
  system: z.string(),
  finding: z.string(),
})
export type RosEntry = z.infer<typeof rosEntrySchema>

export const visitSummarySchema = z.object({
  chiefComplaint: z.string(),
  hpi: z.array(z.string()),
  ros: z.array(rosEntrySchema),
  assessment: z.string(),
  plan: z.string(),
  followUp: z.array(z.string()),
  // No "Level of service" sentence — that renders from the selected `billing`
  // candidate, so the narrative can never contradict the code.
  mdm: z.string().optional(),
})

export const aiConditionSchema = z.object({
  code: z.string(),
  display: z.string(),
  category: z.enum(['problem-list-item', 'encounter-diagnosis']),
  clinicalStatus: z.enum(['active', 'recurrence', 'relapse', 'inactive', 'remission', 'resolved']),
  onsetDateTime: z.string().nullable(),
  severity: z.enum(['mild', 'moderate', 'severe']).nullable(),
  rank: z.number().int().nullable(),
  // PD-2018 — the note words behind the code; absent on drafts made before it.
  evidence: z.string().nullish(),
})

export const aiProcedureSchema = z.object({
  code: z.string(),
  display: z.string(),
  status: z.enum(['preparation', 'in-progress', 'completed', 'not-done']),
  performedDateTime: z.string().nullable(),
})

export const aiMedicationSchema = z.object({
  code: z.string(),
  display: z.string(),
  status: z.enum(['active', 'completed', 'intended', 'stopped', 'on-hold']),
  intent: z.enum(['new-prescription', 'continuation', 'discontinuation']),
  dose: z.string().nullable(),
  route: z.string().nullable(),
  frequency: z.string().nullable(),
  note: z.string().nullable(),
  uncertain: z.boolean().optional(),
  uncertainReason: z.string().optional(),
})

export const aiObservationValueQuantitySchema = z.object({
  value: z.number(),
  unit: z.string(),
})

export const aiObservationSchema = z.object({
  code: z.string(),
  display: z.string(),
  category: z.enum(['vital-signs', 'exam', 'laboratory', 'social-history']),
  valueQuantity: aiObservationValueQuantitySchema.nullable(),
  valueString: z.string().nullable(),
  note: z.string().nullable(),
  implausible: z.boolean().optional(),
  uncertain: z.boolean().optional(),
  uncertainReason: z.string().optional(),
})

export const aiAllergySchema = z.object({
  system: z.string().nullable(),
  code: z.string().nullable(),
  display: z.string(),
  clinicalStatus: z.enum(['active', 'inactive', 'resolved']),
  reaction: z.string().nullable(),
  severity: z.enum(['mild', 'moderate', 'severe']).nullable(),
  category: z.enum(['food', 'medication', 'environment', 'biologic']).nullable(),
  // PD-1141 — id of the known chart allergy this entry UPDATES; null for a
  // genuinely new allergy (the FE derives its `updated` marker from this).
  chartId: z.string().nullable(),
  uncertain: z.boolean().optional(),
  uncertainReason: z.string().optional(),
})
export type AiAllergy = z.infer<typeof aiAllergySchema>

/**
 * PD-1600 — how a low-confidence row is remembered on the note
 * (`soapNote.lowConfidenceFields`) across save / reopen, until the doctor
 * confirms or edits it. Code first, display as the fallback identity.
 */
export function lowConfidenceKey(
  kind: 'medication' | 'allergy' | 'observation',
  code: string | null | undefined,
  display: string | null | undefined,
): string {
  return `${kind}:${(code ?? '').trim() || (display ?? '').trim()}`
}

export const aiServiceRequestSchema = z.object({
  system: z.string(),
  code: z.string(),
  display: z.string(),
  category: z.enum(['laboratory', 'imaging', 'referral']),
  intent: z.enum(['order', 'plan', 'proposal']),
  priority: z.enum(['routine', 'urgent', 'asap', 'stat']).nullable(),
  reasonText: z.string().nullable(),
})

export const billingAddOnSchema = z.object({
  code: z.string(),
  display: z.string(),
  reasoning: z.string(),
})

export const billingCandidateSchema = z.object({
  code: z.string(),
  display: z.string(),
  rank: z.number().int(),
  reasoning: z.string(),
})

export const timeEntrySchema = z.object({
  label: z.string(),
  minutes: z.number().int(),
})

/**
 * Line items ONLY — totals are summed at render and the face-to-face figure is
 * `durationMinutes`. Lives under `billing`, not `summary`, because the judge is
 * told never to grade `billing.*` and these minutes are system-derived, so a
 * transcript check would fail every note.
 */
export const totalTimeSchema = z.object({
  providerEntries: z.array(timeEntrySchema),
  careCoordinatorEntries: z.array(timeEntrySchema),
})

/**
 * PD-2018 — one AI encounter diagnosis on the bill: the note words behind it, the
 * reasons it needs a person's review ('' when the note supports it), and who
 * confirmed it. Keyed by code; the server stamps the confirmation.
 */
export const billingDiagnosisSupportSchema = z.object({
  code: z.string(),
  quote: z.string().default(''),
  review: z.string().default(''),
  confirmedBy: z.string().optional(),
  confirmedAt: z.string().optional(),
})
export type BillingDiagnosisSupport = z.infer<typeof billingDiagnosisSupportSchema>

// Shared with SoapNote.billingSuggestion (whitelist-validated there — every
// field carries a decorator in the DTO, so all stay required here). `totalTime`
// and `diagnoses` are the exceptions: this schema is re-validated when a note is
// locked, so a required field would make every note persisted before them fail to lock.
export const billingSuggestionSchema = z.object({
  candidates: z.array(billingCandidateSchema),
  selectedRank: z.number().int(),
  addOnCodes: z.array(billingAddOnSchema),
  modifiers: z.array(z.string()),
  placeOfService: z.string(),
  durationMinutes: z.number().int(),
  reasoning: z.string(),
  totalTime: totalTimeSchema.optional(),
  diagnoses: z.array(billingDiagnosisSupportSchema).optional(),
})
export type BillingSuggestion = z.infer<typeof billingSuggestionSchema>
export type TimeEntry = z.infer<typeof timeEntrySchema>

// Same shape as the judge's DraftVerificationIssueSchema (severity / category /
// section / description / evidence) — reused rather than re-declared.
export const scribeVerificationIssueSchema = DraftVerificationIssueSchema

// Draft payload without the verification envelope — the base the response
// extends, and the shape of per-round attempt snapshots (avoids self-reference).
const draftPayloadBaseSchema = z.object({
  summary: visitSummarySchema,
  conditions: z.array(aiConditionSchema),
  procedures: z.array(aiProcedureSchema),
  medications: z.array(aiMedicationSchema),
  observations: z.array(aiObservationSchema),
  allergies: z.array(aiAllergySchema),
  serviceRequests: z.array(aiServiceRequestSchema),
  billing: billingSuggestionSchema,
  sessionStartedAt: z.string().nullish(),
})

// One verification-gate round: the judge's verdict on that round's draft.
// `draft` is the full snapshot for NON-chosen rounds only — the chosen round's
// draft IS the response body, so it is never duplicated here. Response-only
// (like the issues: evidence quotes the transcript → PHI, never persisted).
export const scribeVerificationAttemptSchema = z.object({
  /** 0 = first pass, 1..n = regenerations. */
  regeneration: z.number().int(),
  chosen: z.boolean(),
  /** False when the OpenAI judge failed for this round — its issues are then code-validation only. */
  judgeRan: z.boolean(),
  issues: z.array(scribeVerificationIssueSchema),
  summary: z.string(),
  draft: draftPayloadBaseSchema.optional(),
  /** Fields a targeted regeneration rewrote — absent on the first pass and on a whole-note regeneration. */
  patchedFields: z.array(z.string()).optional(),
  /** Wall-clock split for this round: Anthropic generation vs the judge call. */
  generationMs: z.number().int().optional(),
  judgeMs: z.number().int().optional(),
  outputTokens: z.number().int().optional(),
})

export const scribeVerificationSchema = z.object({
  outcome: z.enum(['pass', 'fail', 'unavailable']),
  issues: z.array(scribeVerificationIssueSchema),
  summary: z.string(),
  regenerations: z.number().int(),
  model: z.string(),
  attempts: z.array(scribeVerificationAttemptSchema).optional(),
})

export const generateDraftResponseSchema = draftPayloadBaseSchema.extend({
  verification: scribeVerificationSchema.optional(),
})
export type GenerateDraftResponse = z.infer<typeof generateDraftResponseSchema>

// Assert the outcome enum stays in lockstep with the shared union.
const _outcomeCheck: DraftVerificationOutcome = 'pass' as z.infer<
  typeof scribeVerificationSchema
>['outcome']
void _outcomeCheck
